import { AxiosProgressEvent, AxiosRequestConfig } from 'axios'

import BaseApi from '@/api/Base/BaseApi'
import {
  AddItemsPayload,
  AddItemsResponse,
  AddStorageItemsPayload,
  AddVideoItemsPayload,
  CreateModelPipelinePayload,
  DeleteItemsPayload,
  GetModelSourceItemsResponse,
  ModelSourceNodeInfo,
  UpdateModelSourceNodePayload
} from '@/api/ModelSourceNode/types'
import { CommonListPayload } from '@/api/Node/types'

const ROOT = '/model-source'

const defaultPaginationOptions = {
  itemsPerPage: 40,
  page: 0,
  sortDir: 'desc',
  sortKey: 'createdAt',
}

export default class ModelSourceNodeApi extends BaseApi {
  createModelPipeline(payload: CreateModelPipelinePayload) {
    const { projectId, ...rest } = payload
    return this.agent.post(`/project/${projectId}/model-pipeline/create`, rest)
  }

  getModelSourceNode(modelSourceNodeId: string) {
    return this.agent.get<ModelSourceNodeInfo>(`${ROOT}/${modelSourceNodeId}`)
  }

  updateModelSourceNode(payload: UpdateModelSourceNodePayload) {
    const { modelSourceNodeId, ...rest } = payload
    return this.agent.put<ModelSourceNodeInfo>(`${ROOT}/${modelSourceNodeId}`, rest)
  }

  addItems(payload: AddItemsPayload, onUploadProgress: (progressEvent: AxiosProgressEvent) => void) {
    const { modelSourceNodeId, files } = payload

    const formData = new FormData()
    files.forEach((file) => {
      formData.append('file', file)
    })

    const config: AxiosRequestConfig = {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }

    return this.agent.post<AddItemsResponse>(`${ROOT}/${modelSourceNodeId}/add-items`, formData, config)
  }

  addStorageItems(payload: AddStorageItemsPayload) {
    const { modelSourceNodeId, ...rest } = payload
    return this.agent.post<ModelSourceNodeInfo>(`${ROOT}/${modelSourceNodeId}/add-storage-items`, rest)
  }

  addVideoItems(payload: AddVideoItemsPayload, onUploadProgress: (progressEvent: AxiosProgressEvent) => void) {
    const { modelSourceNodeId, files, fps } = payload

    const formData = new FormData()
    files.forEach((file) => {
      formData.append('file', file)
    })
    formData.append('fps', fps)

    const config: AxiosRequestConfig = {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }
    return this.agent.post<AddItemsResponse>(`${ROOT}/${modelSourceNodeId}/add-video`, formData, config)
  }

  deleteItems(payload: DeleteItemsPayload) {
    const { modelSourceNodeId, ...rest } = payload
    return this.agent.delete(`${ROOT}/${modelSourceNodeId}/delete-items`, { data: rest })
  }

  getModelSourceItems(modelSourceNodeId: string, options?: CommonListPayload) {
    const { itemsPerPage, page, sortDir, sortKey } = options ?? defaultPaginationOptions
    return this.agent.get<GetModelSourceItemsResponse>(`${ROOT}/${modelSourceNodeId}/items`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }
}
